import * as React           from "react";
import {Types}              from "@Common/types";

import "./search-route.scss";

import {bindInput}             from "@Common/react/bind-input";
import {cs}                    from "@Common/react/chain-services";
import {consumeContext}        from "@Common/react/context";
import {DebounceCache}         from "@Common/react/debounce-cache";
import {KeyedState}            from "@Common/react/keyed-state";
import {rLsStore}              from "@Common/react/ls-store";
import {QueryString}           from "@Common/react/query-string";
import {RangeLoader}           from "@Common/react/range-loader";
import {UseState}              from "@Common/react/use-state";
import {Responsive}            from "@Common/ui-components/responsive";
import {flatten1, isEmpty}     from "@Common/utils/collections";
import {oMapToArr}             from "@Common/utils/objects";

import {TemplateListMode}           from "../common/components/template-list-mode/template-list-mode";
import {rTemplates}                 from "../common/components/templates/templates";
import {AGENT_LEVEL, BRANCH_LEVEL}  from "../common/logic/user-level";
import {Layout}                     from "../layout/layout";


export const SearchRoute = ({}): any => cs(
    ["search", (_, next) => QueryString({
        param: "q",
        next,
    })],
    consumeContext("apis"),
    consumeContext("catSubStyle"),
    consumeContext("company"),
    consumeContext("session"),
    consumeContext("userLevels"),
    ["catSubStyle", ({catSubStyle}, next) => next(catSubStyle?.value)],

    ["limit", ({search}, next) => KeyedState({
        defaultValue: 50,
        _key: search.value,
        next,
    })],

    ["styleTemplates", ({search, limit, apis, company, session, userLevels}, next) => RangeLoader({
        _key: search.value,
        range: {from: 0, to: limit.value},
        load: async (range) => {
            const map = await apis.next.searchTemplates({query: search.value ? search.value : " ", limit: range.to - range.from, skip: range.from});
            const templates  = flatten1(oMapToArr(map, (s, styleId) => s.templates.map((t) => ({
                ...t,
                styleId
            }))));
            const hasResults = !isEmpty(templates);
            apis.next.omniSearch.addSearchHistoryRecord({record:{
                "action": "search",
                "date_created": new Date().toISOString(),
                "has_results": hasResults,
                "id_agent": userLevels.userLevel === AGENT_LEVEL ? session?.data?.agent?.idAgent : "",
                "id_branch": userLevels.userLevel >= BRANCH_LEVEL ? session?.data?.branch?.idBranch : "",
                "id_company": company.idCompany,
                "id_user": userLevels?.idUser?.toString() ?? "0",
                "search_handler": "templates",
                "search_term": search.value,
                "suggestion_name": "",
                "suggestion_type": hasResults ? "search" : "none",
                "suggestion_data": search.value,
            }});
            return templates;
        },
        next,
    })],

    // @ts-ignore
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>],
    ["viewMode", ({responsive}, next) => responsive.lt("md") ? next({value: "large"}) : rLsStore("tiles.viewMode", "medium")({}, next)],
    ({search, styleTemplates, catSubStyle, viewMode, limit, apis, company, session, userLevels}) => {

        const templates = flatten1(styleTemplates.list);

        return (
            <Layout className="search-route-3ec">
                <div className="restrict-width-1">
                    {cs(
                        ["focused", (_, next) => UseState({next})],
                        ["debounce", (_, next) => DebounceCache({
                            state: search,
                            next,
                        })],
                        ({focused, debounce}) => (
                            <input
                                {...bindInput(debounce.state)}
                                placeholder={focused.value ? "Please enter any keyword to search" : "Search"}
                                xd-qa-type="text_field"
                                xd-qa-name="search"
                                onFocus={() => focused.onChange(true)}
                                onBlur={() => {
                                    debounce.flush();
                                    focused.onChange(false);
                                }}
                            />
                        )
                    )}

                    <div className="controls">
                        <div className="view-modes">
                            {TemplateListMode(viewMode)}
                        </div>
                    </div>

                    {isEmpty(templates) && !styleTemplates.loading ? (
                        <div className="no-result" xd-qa-type="label" xd-qa-name="Empty">
                            0 results for "{search.value}"
                        </div>
                    ) : (!templates.length && styleTemplates.loading) ? (
                        <div className="loading" xd-qa-type="label" xd-qa-name="Loading">
                            Loading...
                        </div>
                    ) : (
                        <div className="list">
                            {!!templates.length && rTemplates(
                                templates,
                                {
                                    catId: (styleId) => {
                                        const catSub: { cat: Types.Apis.Models.Category, sub: Types.Apis.Models.Subcategory } = getCatSub(styleId, catSubStyle);
                                        return catSub && catSub.cat.id;
                                    },
                                    subId: (styleId) => {
                                        const catSub: { cat: Types.Apis.Models.Category, sub: Types.Apis.Models.Subcategory } = getCatSub(styleId, catSubStyle);
                                        return catSub && catSub.sub.id;
                                    },
                                    mode: viewMode.value,
                                    onNavigate: (route, name, template) => apis.next.omniSearch.addSearchHistoryRecord({record:{
                                        "action": "click",
                                        "date_created": new Date().toISOString(),
                                        "has_results": true,
                                        "id_agent": userLevels.userLevel === AGENT_LEVEL ? session?.data?.agent?.idAgent : "",
                                        "id_branch": userLevels.userLevel >= BRANCH_LEVEL ? session?.data?.branch?.idBranch : "",
                                        "id_company": company.idCompany,
                                        "id_user": userLevels?.idUser?.toString() ?? "0",
                                        "search_handler": "templates",
                                        "search_term": search.value,
                                        "suggestion_name": name,
                                        "suggestion_type": "template",
                                        "suggestion_data": template.id,
                                    }}),
                                }
                            )}

                            {(styleTemplates.loading || limit.value === templates.length) && (
                                <div className="load-more">
                                    <button
                                        onClick={() => limit.onChange(limit.value + 50)}
                                    >
                                        Load More
                                        {" "}
                                        {styleTemplates.loading && (
                                            <i className="fa fa-spinner fa-pulse"/>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </Layout>
        );
    },
);

const getCatSub = (styleId: string, catSubStyle: Types.Apis.Models.Category[]): {cat: Types.Apis.Models.Category, sub: Types.Apis.Models.Subcategory} => {
    for (const catId in catSubStyle) {
        const cat: Types.Apis.Models.Category = catSubStyle[catId];
        for (let i: number = 0; i < cat.child.length; i++) {
            const sub: Types.Apis.Models.Subcategory = cat.child[i];
            if (sub.child.find((style) => style.id == styleId)) {
                cat.id = catId;
                return {cat, sub};
            }
        }
    }
};

const isSystemCategory = (category: Types.Apis.Models.Category): boolean => category.categoryType?.includes("system|");
